import { useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Document, Page, pdfjs } from 'react-pdf'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw, Download } from 'lucide-react'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// PDF.js worker の設定（ローカルファイルを使用してCSPエラーを回避）
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

interface PdfViewerProps {
  url: string
  fileName: string
  isOpen: boolean
  onClose: () => void
  onDownload?: () => void
}

export default function PdfViewer({ url, fileName, isOpen, onClose, onDownload }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rotation, setRotation] = useState<number>(0)

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setLoading(false)
    setError(null)
  }, [])

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF load error:', error)
    setError('PDFの読み込みに失敗しました')
    setLoading(false)
  }, [])

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1))
  }

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages))
  }

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360)
  }

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-95 flex flex-col">
      {/* ヘッダー */}
      <div className="flex items-center justify-between p-2 sm:p-4 bg-gray-900 text-white">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full flex-shrink-0"
            aria-label="閉じる"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          <span className="text-sm sm:text-base truncate">{fileName}</span>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {onDownload && (
            <button
              onClick={onDownload}
              className="p-2 hover:bg-gray-700 rounded-full"
              aria-label="ダウンロード"
            >
              <Download className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={handleRotate}
            className="p-2 hover:bg-gray-700 rounded-full"
            aria-label="回転"
          >
            <RotateCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="flex-1 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mx-auto mb-2"></div>
              <p>読み込み中...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <div className="text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-700 rounded hover:bg-gray-600"
              >
                閉じる
              </button>
            </div>
          </div>
        )}

        {!error && (
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={4}
            centerOnInit
            wheel={{ step: 0.1 }}
            pinch={{ step: 5 }}
            doubleClick={{ mode: 'reset' }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                {/* ズームコントロール（デスクトップ向け） */}
                <div className="absolute top-4 right-4 z-10 hidden sm:flex flex-col gap-2">
                  <button
                    onClick={() => zoomIn()}
                    className="p-2 bg-gray-800 bg-opacity-70 hover:bg-opacity-100 text-white rounded-full"
                    aria-label="ズームイン"
                  >
                    <ZoomIn className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => zoomOut()}
                    className="p-2 bg-gray-800 bg-opacity-70 hover:bg-opacity-100 text-white rounded-full"
                    aria-label="ズームアウト"
                  >
                    <ZoomOut className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => resetTransform()}
                    className="p-2 bg-gray-800 bg-opacity-70 hover:bg-opacity-100 text-white rounded-full text-xs"
                    aria-label="リセット"
                  >
                    1:1
                  </button>
                </div>

                <TransformComponent
                  wrapperStyle={{
                    width: '100%',
                    height: '100%',
                  }}
                  contentStyle={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Document
                    file={url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading=""
                    className="flex justify-center items-center"
                  >
                    <Page
                      pageNumber={pageNumber}
                      rotate={rotation}
                      loading=""
                      className="shadow-2xl"
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      width={Math.min(window.innerWidth * 0.9, 800)}
                    />
                  </Document>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        )}
      </div>

      {/* ページナビゲーション */}
      {numPages > 0 && (
        <div className="flex items-center justify-center gap-4 p-3 sm:p-4 bg-gray-900 text-white">
          <button
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            className="p-2 sm:p-3 bg-gray-800 rounded-full hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="前のページ"
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>

          <div className="text-sm sm:text-base">
            <span className="font-medium">{pageNumber}</span>
            <span className="text-gray-400"> / {numPages}</span>
          </div>

          <button
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            className="p-2 sm:p-3 bg-gray-800 rounded-full hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="次のページ"
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>
      )}
    </div>,
    document.body
  )
}
